// Saved settings and progress (mute, Daily results, streak, tips seen...).
//
// Website, apps, Discord and other portals: the browser's localStorage, as before.
// Playgama: kept in memory and saved through the Bridge storage module, which Playgama requires
// (requirement 3.2.1: no localStorage). portal.ts loads the saved values before the first screen.
// CrazyGames: the SDK's Data module (same API as localStorage) once the SDK is up, so progress
// follows a logged-in player across devices (CrazyGames "Progress save"; guests are kept locally
// by the SDK itself). Needs "Progress Save: Data module" ticked in the submission form; if it isn't
// (or the SDK is missing), everything stays in localStorage as before.

const ON_BRIDGE = process.env.REACT_APP_PORTAL === "playgama";
const INDEX_KEY = "rafiki_keys";

const mem: { [k: string]: string } = {};
let bridgeReady = false;
const dirty = new Set<string>();
let flushTimer: any = null;

const bridgeStorage = () => (window as any).bridge?.storage;

const ON_CRAZYGAMES = process.env.REACT_APP_PORTAL === "crazygames";
let cgData: any = null;
const local = {
  getItem(k: string): string | null {
    try {
      return window.localStorage.getItem(k);
    } catch (e) {
      return null;
    }
  },
  setItem(k: string, v: string) {
    try {
      window.localStorage.setItem(k, v);
    } catch (e) {}
  },
  removeItem(k: string) {
    try {
      window.localStorage.removeItem(k);
    } catch (e) {}
  },
};
// The Data module throws (e.g. dataModuleDisabled) when it can't be used: fall back to localStorage for good.
const viaCg = <T>(fn: (d: any) => T, fallback: () => T): T => {
  if (cgData) {
    try {
      return fn(cgData);
    } catch (e) {
      cgData = null;
    }
  }
  return fallback();
};

// CrazyGames: called by portal.ts once SDK.init() has finished.
export const enableCrazyGamesData = () => {
  if (!ON_CRAZYGAMES) return;
  try {
    const d = (window as any).CrazyGames?.SDK?.data;
    if (!d || typeof d.getItem !== "function" || typeof d.setItem !== "function") return;
    d.getItem("rafiki.muted"); // throws if the Data module is switched off for the game
    cgData = d;
  } catch (e) {
    cgData = null;
  }
};
export const usingCrazyGamesData = () => Boolean(cgData);

const flush = () => {
  flushTimer = null;
  const st = bridgeStorage();
  if (!bridgeReady || !st || !dirty.size) return;
  const keys = Array.from(dirty);
  dirty.clear();
  const setKeys = keys.filter((k) => k in mem);
  const delKeys = keys.filter((k) => !(k in mem));
  try {
    st.set([...setKeys, INDEX_KEY], [...setKeys.map((k) => mem[k]), JSON.stringify(Object.keys(mem))]).catch(() => {});
    if (delKeys.length) st.delete(delKeys).catch(() => {});
  } catch (e) {}
};
const schedule = (k: string) => {
  dirty.add(k);
  if (!flushTimer) flushTimer = setTimeout(flush, 300);
};

export const store = {
  getItem(k: string): string | null {
    if (ON_BRIDGE) return k in mem ? mem[k] : null;
    if (ON_CRAZYGAMES) return viaCg(
      (d) => {
        const v = d.getItem(k);
        return v === null || v === undefined ? null : String(v);
      },
      () => local.getItem(k)
    );
    return local.getItem(k);
  },
  setItem(k: string, v: string) {
    if (ON_BRIDGE) {
      mem[k] = String(v);
      schedule(k);
      return;
    }
    if (ON_CRAZYGAMES) return viaCg((d) => void d.setItem(k, String(v)), () => local.setItem(k, v));
    local.setItem(k, v);
  },
  removeItem(k: string) {
    if (ON_BRIDGE) {
      delete mem[k];
      schedule(k);
      return;
    }
    if (ON_CRAZYGAMES) return viaCg((d) => void d.removeItem(k), () => local.removeItem(k));
    local.removeItem(k);
  },
};

// Playgama: read everything saved earlier from the Bridge, then start saving through it.
export const loadBridgeStore = async () => {
  if (!ON_BRIDGE) return;
  const st = bridgeStorage();
  try {
    if (st) {
      const [index] = await st.get([INDEX_KEY]);
      const keys: string[] = (() => {
        try {
          // The Bridge hands back JSON-looking values already parsed (the key list comes back as
          // an array, not a string), so only parse what is still text. Before 1 Nov 2026 this
          // parsed the array again, which threw, and nothing saved was ever restored.
          const v = typeof index === "string" ? JSON.parse(index || "[]") : index;
          return Array.isArray(v) ? v.filter((k) => typeof k === "string") : [];
        } catch (e) {
          return [];
        }
      })();
      if (keys.length) {
        const values = await st.get(keys);
        keys.forEach((k, i) => {
          const v = values?.[i];
          if (v !== null && v !== undefined && !(k in mem)) mem[k] = typeof v === "string" ? v : JSON.stringify(v);
        });
      }
    }
  } catch (e) {}
  bridgeReady = true;
  flush();
};

// Tests only.
export const _resetStoreForTests = () => {
  Object.keys(mem).forEach((k) => delete mem[k]);
  bridgeReady = false;
  dirty.clear();
  cgData = null;
};
